import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Form, InputGroup, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import DetalheImovel from './imoveis/DetalheImovel';
import TabelaImoveis from './imoveis/TabelaImoveis';

// Réplica dos 3 cards de contagem do dashboard antigo (home.blade.php):
// Clientes, Imóveis e Usuários — este último só nivel 1/2 (nivel 3/corretor
// não via esse card no original). Usuários fica de fora pra nivel 1 nesta
// v1 porque a rota exige escolher uma empresa (`empresa_id`) e ainda não
// existe seletor de empresa na tela — adicionar quando o módulo Empresas
// for construído.
//
// Cada card agora é um link pra sua lista (pedido do Jason, 21/10/2026: "X Imóveis" -> /imoveis),
// e a busca geral abaixo procura ao mesmo tempo em Imóveis/Clientes/Leads (`q`, mesmo parâmetro
// que as telas de lista já usam) — resultado em abas, com link direto pra cada item e um "ver
// todos" que abre a lista já filtrada (ListaImoveis/Clientes/Leads leem `?q=` da URL na primeira
// carga, ver useSearchParams nelas).
const POR_PAGINA_BUSCA = 6;

export default function Dashboard() {
  const { usuario } = useAuth();
  const toast = useToast();
  const [contagens, setContagens] = useState(null);
  const [erro, setErro] = useState(false);

  const [termo, setTermo] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [resultados, setResultados] = useState(null); // { imoveis, clientes, leads } | null
  const [abaAtiva, setAbaAtiva] = useState('imoveis');
  // Clicar num imóvel da busca abre a mesma janela rápida da listagem (pedido do Jason,
  // 21/10/2026) — clientes/leads não têm esse modal na lista deles, então continuam indo
  // direto pra página.
  const [imovelAberto, setImovelAberto] = useState(null);
  // "Ver imóveis" de um cliente da busca (22/10/2026) — SUBSTITUI o conteúdo da aba Imóveis
  // (em vez do resultado do termo buscado) enquanto estiver com um cliente selecionado; `null`
  // volta a mostrar o resultado normal da busca.
  const [imoveisDoCliente, setImoveisDoCliente] = useState(null); // { nome, data } | null
  const [buscandoImoveisDoCliente, setBuscandoImoveisDoCliente] = useState(false);

  useEffect(() => {
    async function carregar() {
      setErro(false);
      try {
        // `per_page` não é respeitado pela API ainda (sempre usa o
        // default) — só o `meta.total` importa aqui, então não faz
        // diferença pedir mais.
        const [imoveis, clientes] = await Promise.all([api.get('/imoveis'), api.get('/clientes')]);
        const dados = { imoveis: imoveis.meta.total, clientes: clientes.meta.total };
        if (usuario.nivel === 2) {
          const usuarios = await api.get('/usuarios');
          dados.usuarios = usuarios.data.length;
        }
        setContagens(dados);
      } catch (err) {
        // Achado em auditoria 2026-09-18: sem isso, qualquer falha (sessão
        // expirada, rede, 500 do backend) deixava a tela presa num
        // spinner infinito pra sempre — é a primeira tela depois do
        // login, todo mundo passa por aqui.
        setErro(true);
        toast.erro(err.message || 'Não foi possível carregar o dashboard');
      }
    }
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario.nivel]);

  async function buscar(e) {
    e.preventDefault();
    const q = termo.trim();
    setImoveisDoCliente(null); // nova busca cancela qualquer "Ver imóveis" anterior
    if (!q) {
      setResultados(null);
      return;
    }
    setBuscando(true);
    const query = new URLSearchParams({ q, per_page: POR_PAGINA_BUSCA });
    try {
      const [imoveis, clientes, leads] = await Promise.all([
        api.get(`/imoveis?${query}`).catch(() => ({ data: [], meta: { total: 0 } })),
        api.get(`/clientes?${query}`).catch(() => ({ data: [], meta: { total: 0 } })),
        api.get(`/leads?${query}`).catch(() => ({ data: [], meta: { total: 0 } })),
      ]);
      setResultados({ q, imoveis, clientes, leads });
      // abre direto na primeira aba que tiver resultado, pra não ter que clicar à toa
      if (imoveis.meta.total > 0) setAbaAtiva('imoveis');
      else if (clientes.meta.total > 0) setAbaAtiva('clientes');
      else if (leads.meta.total > 0) setAbaAtiva('leads');
    } catch (err) {
      toast.erro(err.message || 'Não foi possível buscar agora');
    } finally {
      setBuscando(false);
    }
  }

  function limparBusca() {
    setTermo('');
    setResultados(null);
    setImoveisDoCliente(null);
  }

  // "Ver imóveis" de um cliente da busca (pedido do Jason, 22/10/2026) — busca os imóveis onde
  // ele é proprietário e mostra isso NA ABA IMÓVEIS, no lugar do resultado do termo buscado.
  async function verImoveisDoCliente(cliente) {
    setAbaAtiva('imoveis');
    setBuscandoImoveisDoCliente(true);
    try {
      const r = await api.get(`/imoveis?proprietario_id=${cliente.public_id}&per_page=100`);
      setImoveisDoCliente({ nome: cliente.c1_nome, data: r.data });
    } catch (err) {
      toast.erro(err.message || 'Não foi possível buscar os imóveis deste cliente');
    } finally {
      setBuscandoImoveisDoCliente(false);
    }
  }

  if (erro) return null;
  if (!contagens) return <Spinner animation="border" />;

  const totalResultados = resultados ? resultados.imoveis.meta.total + resultados.clientes.meta.total + resultados.leads.meta.total : 0;

  return (
    <>
      <h1 className="h4 mb-4">Dashboard</h1>

      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={buscar}>
            <InputGroup>
              <Form.Control
                type="search"
                placeholder="Buscar em imóveis, clientes e leads…"
                value={termo}
                onChange={(e) => setTermo(e.target.value)}
                aria-label="Busca geral"
              />
              <Button type="submit" variant="primary" disabled={buscando}>
                {buscando ? <Spinner animation="border" size="sm" /> : 'Buscar'}
              </Button>
              {resultados && (
                <Button type="button" variant="outline-secondary" onClick={limparBusca}>
                  Limpar
                </Button>
              )}
            </InputGroup>
          </Form>

          {resultados && totalResultados === 0 && (
            <Alert variant="light" className="mt-3 mb-0 border">
              Nada encontrado pra "{resultados.q}" em imóveis, clientes ou leads.
            </Alert>
          )}

          {resultados && totalResultados > 0 && (
            <Tabs activeKey={abaAtiva} onSelect={(k) => setAbaAtiva(k)} className="mt-3">
              <Tab
                eventKey="imoveis"
                title={
                  <>
                    Imóveis <Badge bg="secondary">{imoveisDoCliente ? imoveisDoCliente.data.length : resultados.imoveis.meta.total}</Badge>
                  </>
                }
              >
                {buscandoImoveisDoCliente ? (
                  <Spinner animation="border" size="sm" className="mt-2" />
                ) : imoveisDoCliente ? (
                  <div className="mt-2">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted small">Imóveis de <strong>{imoveisDoCliente.nome}</strong></span>
                      <Button size="sm" variant="link" className="p-0" onClick={() => setImoveisDoCliente(null)}>
                        ← voltar pra busca
                      </Button>
                    </div>
                    {imoveisDoCliente.data.length === 0 ? (
                      <p className="text-muted mb-0">Nenhum imóvel vinculado.</p>
                    ) : (
                      <TabelaImoveis imoveis={imoveisDoCliente.data} usuario={usuario} aoAbrir={setImovelAberto} />
                    )}
                  </div>
                ) : (
                  <ResultadoImoveis resultado={resultados.imoveis} q={resultados.q} usuario={usuario} aoAbrir={setImovelAberto} />
                )}
              </Tab>
              <Tab
                eventKey="clientes"
                title={
                  <>
                    Clientes <Badge bg="secondary">{resultados.clientes.meta.total}</Badge>
                  </>
                }
              >
                <ResultadoClientes resultado={resultados.clientes} q={resultados.q} aoVerImoveis={verImoveisDoCliente} />
              </Tab>
              <Tab
                eventKey="leads"
                title={
                  <>
                    Leads <Badge bg="secondary">{resultados.leads.meta.total}</Badge>
                  </>
                }
              >
                <ResultadoLeads resultado={resultados.leads} q={resultados.q} />
              </Tab>
            </Tabs>
          )}
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col md={4}>
          <CardContagem to="/imoveis" bg="primary" valor={contagens.imoveis} rotulo="Imóveis" />
        </Col>
        <Col md={4}>
          <CardContagem to="/clientes" bg="success" valor={contagens.clientes} rotulo="Clientes" />
        </Col>
        {contagens.usuarios !== undefined && (
          <Col md={4}>
            <CardContagem to="/usuarios" bg="warning" valor={contagens.usuarios} rotulo="Usuários" />
          </Col>
        )}
      </Row>

      <DetalheImovel publicId={imovelAberto} aoFechar={() => setImovelAberto(null)} aoExcluido={() => setImovelAberto(null)} />
    </>
  );
}

// Card de contagem clicável — leva pra lista correspondente (pedido do Jason, 21/10/2026).
function CardContagem({ to, bg, valor, rotulo }) {
  return (
    <Card as={Link} to={to} bg={bg} text={bg === 'warning' ? undefined : 'white'} className="text-decoration-none" style={{ cursor: 'pointer' }}>
      <Card.Body>
        <div className="fs-2 fw-bold">{valor}</div>
        <div>{rotulo}</div>
      </Card.Body>
    </Card>
  );
}

function VerTodos({ to, total, mostrados }) {
  if (total <= mostrados) return null;
  return (
    <div className="mt-2">
      <Link to={to}>Ver todos os {total} resultados →</Link>
    </div>
  );
}

function ResultadoImoveis({ resultado, q, usuario, aoAbrir }) {
  if (resultado.meta.total === 0) return <p className="text-muted mb-0 mt-2">Nenhum imóvel encontrado.</p>;
  return (
    <>
      <div className="mt-2">
        <TabelaImoveis imoveis={resultado.data} usuario={usuario} aoAbrir={aoAbrir} />
      </div>
      <VerTodos to={`/imoveis?q=${encodeURIComponent(q)}`} total={resultado.meta.total} mostrados={resultado.data.length} />
    </>
  );
}

function ResultadoClientes({ resultado, q, aoVerImoveis }) {
  if (resultado.meta.total === 0) return <p className="text-muted mb-0 mt-2">Nenhum cliente encontrado.</p>;
  return (
    <>
      <div className="list-group mt-2">
        {resultado.data.map((cli) => {
          const totalImoveis = cli.imoveis_count || 0;
          return (
            <div key={cli.public_id} className="list-group-item d-flex justify-content-between align-items-center gap-2">
              <Link to={`/clientes/${cli.public_id}`} className="text-decoration-none flex-grow-1">
                <div className="fw-bold">{cli.c1_nome}</div>
                <div className="small text-muted">{cli.c1_email || cli.c1_celular || cli.c1_telefone || ''}</div>
              </Link>
              {/* Sem imóvel vinculado: nem mostra o botão (pedido do Jason, 22/10/2026) */}
              {totalImoveis > 0 && (
                <Button size="sm" variant="outline-primary" className="text-nowrap" onClick={() => aoVerImoveis(cli)}>
                  {totalImoveis} imóve{totalImoveis === 1 ? 'l' : 'is'} — Ver
                </Button>
              )}
            </div>
          );
        })}
      </div>
      <VerTodos to={`/clientes?q=${encodeURIComponent(q)}`} total={resultado.meta.total} mostrados={resultado.data.length} />
    </>
  );
}

function ResultadoLeads({ resultado, q }) {
  if (resultado.meta.total === 0) return <p className="text-muted mb-0 mt-2">Nenhum lead encontrado.</p>;
  return (
    <>
      <div className="list-group mt-2">
        {resultado.data.map((lead) => (
          <Link key={lead.public_id} to={`/leads/${lead.public_id}`} className="list-group-item list-group-item-action">
            <div className="fw-bold">{lead.l1_nome || '(sem nome)'}</div>
            <div className="small text-muted">{lead.l1_email || lead.l1_telefone || ''}</div>
          </Link>
        ))}
      </div>
      <VerTodos to={`/leads?q=${encodeURIComponent(q)}`} total={resultado.meta.total} mostrados={resultado.data.length} />
    </>
  );
}
